// VerificationSchedulePage.jsx
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, Factory } from 'lucide-react';

import FgisArshinTab from './FgisArshinTab';
import MeteringUnitsTab from './MeteringUnitsTab';

const DEFAULT_TAB = 'metering'; // Если нужно открывать Аршин по умолчанию, поставьте 'fgis-arshin'

const TABS = [
    {
        key: 'metering',
        label: 'Поверка узлов учета',
        icon: Factory,
        description: 'Планирование и контроль поверок узлов учета',
    },
    {
        key: 'fgis-arshin',
        label: 'Проверка поверок ФГИС «Аршин»',
        icon: ClipboardCheck,
        description: 'Проверка сведений о поверках в реестре ФГИС «Аршин»',
    },
];

export default function VerificationSchedulePage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const tabFromUrl = searchParams.get('tab');

    const activeTab = useMemo(() => {
        return TABS.some(({ key }) => key === tabFromUrl)
            ? tabFromUrl
            : DEFAULT_TAB;
    }, [tabFromUrl]);

    useEffect(() => {
        document.title = 'График поверок';
    }, []);

    const changeTab = (key) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', key);
        setSearchParams(nextSearchParams, { replace: true });
    };

    return (
        <div className="space-y-4 sm:space-y-5">
            {/* Заголовок страницы */}
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white">
                    График поверок
                </h1>
                <p className="mt-1 text-sm text-slate-400">
                    Контроль сроков поверок и проверка сведений в ФГИС «Аршин»
                </p>
            </div>

            {/* Вкладки */}
            <div
                role="tablist"
                aria-label="Разделы графика поверок"
                className="
                    flex flex-col gap-2 sm:flex-row sm:flex-wrap
                    rounded-xl border border-slate-700 bg-slate-800 p-1.5
                "
            >
                {TABS.map(({ key, label, icon: Icon, description }) => {
                    const isActive = activeTab === key;

                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            id={`verification-tab-${key}`}
                            aria-selected={isActive}
                            aria-controls={`verification-panel-${key}`}
                            tabIndex={isActive ? 0 : -1}
                            title={description}
                            onClick={() => changeTab(key)}
                            className={`
                                flex flex-1 sm:flex-none items-center justify-center gap-2
                                rounded-lg px-4 py-2.5 text-sm font-medium border transition
                                ${isActive
                                    ? 'border-blue-500/40 bg-blue-600/20 text-blue-300'
                                    : 'border-transparent bg-transparent text-slate-400 hover:bg-slate-700/60 hover:text-white'
                                }
                            `}
                        >
                            <Icon className="h-4 w-4" />
                            <span>{label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Активная вкладка */}
            {activeTab === 'metering' && (
                <div
                    role="tabpanel"
                    id="verification-panel-metering"
                    aria-labelledby="verification-tab-metering"
                >
                    <MeteringUnitsTab />
                </div>
            )}

            {activeTab === 'fgis-arshin' && (
                <div
                    role="tabpanel"
                    id="verification-panel-fgis-arshin"
                    aria-labelledby="verification-tab-fgis-arshin"
                >
                    <FgisArshinTab />
                </div>
            )}
        </div>
    );
}